import { VAS_BANDS } from "../vasScale";

/**
 * Adapters between ComplaintForm's CC v2 body shape (camelCase, sent to
 * api-cc-v2-service today) and the transcript's captured-item payload
 * shape (snake_case, matches what the Python agent emits and what Go's
 * SaveSection fan-out reads on Save & next).
 *
 * ComplaintForm is now the one editor used by both the standalone Chief
 * Complaints tab and the AI transcript modal. In transcript mode the
 * caller passes `onSave` and receives the body in CC v2 shape; these
 * helpers convert it to the captured-item shape before POST /captured
 * / decideCaptured. Keeps the form component ignorant of any
 * transcript-specific vocabulary.
 *
 *   CC v2 add body        → captured "add" payload
 *   {complaint, complaintCode:{icdCode, label?}, vas, sinceWhen,
 *    description, consultedDoctor}
 *      →
 *   {complaint, icd_code, icd_description, intensity, since_date,
 *    description, consulted_doctor}
 *
 *   CC v2 update body     → captured "update" payload
 *   {vas, sinceWhen, description, consultedDoctor}
 *      →
 *   {complaint, icd_code, vas_band, vas_min, since_when, description,
 *    consulted_doctor}
 *
 * The update payload additionally carries `complaint` + `icd_code`
 * pulled from the CC v2 record being edited, because the update card
 * doesn't collect them (the ICD/name are read-only in update mode)
 * but the review table + AdaptChiefComplaintUpdate on the Go side
 * need them for display + provenance.
 */

// The band a stored VAS score sits in — mirrors ComplaintForm's own
// bandForScore. Used to derive `vas_band` from the numeric VAS the
// form emits.
const bandForScore = (vas) =>
  VAS_BANDS.find((b) => b.min <= vas && vas <= b.max) || VAS_BANDS[0];

// ISO datetime like "2026-08-11T00:00:00.000Z" → date-only "2026-08-11".
// The captured item's since_date / since_when fields carry the plain
// date; Go's toISOUTC helper coerces it back to RFC3339 on the CC v2
// write path.
const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

/**
 * CC v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const cc = body.complaintCode || {};
  return {
    complaint:        body.complaint || "",
    icd_code:         cc.icdCode || "",
    icd_description:  cc.label || cc.icdDescription || "",
    intensity:        bandForScore(body.vas).key,
    since_date:       toIsoDate(body.sinceWhen),
    description:      body.description || "",
    consulted_doctor: !!body.consultedDoctor,
  };
}

/**
 * CC v2 update body → captured "update" payload. `context` carries the
 * complaint name + ICD code from the CC v2 record being edited (or from
 * the staged captured payload for a re-edit), pulled through so the
 * review table can render "Update · <name>" without a second lookup and
 * so the Go adapter has the ICD for its own writes.
 */
export function bodyToCapturedUpdate(body, context) {
  const ctx = context || {};
  const cc = ctx.complaintCode || {};
  const band = bandForScore(body.vas);
  return {
    complaint:        ctx.complaint || "",
    icd_code:         cc.icdCode || ctx.icd_code || "",
    vas_band:         band.key,
    vas_min:          body.vas,
    since_when:       toIsoDate(body.sinceWhen),
    description:      body.description || "",
    consulted_doctor: !!body.consultedDoctor,
  };
}
